import { useEffect, useRef, useState } from "react";
import { ShieldCheck } from "lucide-react";
import { useSearchParams } from "react-router-dom";

import { authorizeBrowserClient } from "../../api/auth";
import { apiUrl } from "../../config";
import { getSessionToken } from "../../services/AuthSession";
import AuthPageLayout from "../../components/Authentication/AuthPageLayout";

const BROWSER_AUTH_RETURN_KEY = "photon_browser_authorization_return";

export const buildBrowserAuthorizationCallback = (redirectUri, state, code) => {
  const callback = new URL(redirectUri);
  callback.searchParams.set("code", code);
  callback.searchParams.set("state", state);
  callback.searchParams.set(
    "api_url",
    new URL(apiUrl || window.location.origin, window.location.origin).origin,
  );
  return callback.toString();
};

const BrowserAuthorizationPage = () => {
  const [searchParams] = useSearchParams();
  const [error, setError] = useState("");
  const authorizationRequestRef = useRef(null);

  useEffect(() => {
    const requestKey = searchParams.toString();
    if (authorizationRequestRef.current === requestKey) return;
    authorizationRequestRef.current = requestKey;
    sessionStorage.removeItem(BROWSER_AUTH_RETURN_KEY);
    const authorize = async () => {
      const redirectUri = searchParams.get("redirect_uri");
      const state = searchParams.get("state");
      const codeChallenge = searchParams.get("code_challenge");
      const codeChallengeMethod = searchParams.get("code_challenge_method");
      const scopes = (searchParams.get("scope") || "")
        .split(" ")
        .filter(Boolean);
      const sessionToken = getSessionToken();

      if (
        !redirectUri ||
        !state ||
        !codeChallenge ||
        codeChallengeMethod !== "S256" ||
        !scopes.length ||
        !sessionToken
      ) {
        setError("The extension authorization request is invalid.");
        return;
      }

      try {
        const { code } = await authorizeBrowserClient(sessionToken, {
          redirect_uri: redirectUri,
          scopes,
          code_challenge: codeChallenge,
          code_challenge_method: codeChallengeMethod,
        });
        window.location.replace(buildBrowserAuthorizationCallback(redirectUri, state, code));
      } catch (authorizationError) {
        setError(authorizationError.message || "Extension authorization failed.");
      }
    };

    authorize();
  }, [searchParams]);

  return (
    <AuthPageLayout>
      <section className="photon-content-card w-full max-w-sm bg-surface-card border rounded-lg p-7 text-center shadow-sm">
        <ShieldCheck className="mx-auto mb-4 text-ink-primary dark:text-ink-accent" size={32} />
        <h1 className="photon-body-text text-lg font-semibold">Photon browser access</h1>
        <p className="photon-muted-text mt-2 text-sm">
          {error || "Connecting the Photon extension to your signed-in account..."}
        </p>
      </section>
    </AuthPageLayout>
  );
};

export default BrowserAuthorizationPage;